import { useCallback, useState } from 'react';
import { Minus, Plus } from 'lucide-react';
import { cn } from '@/shared/utils/cn';
import type { CustomizationService } from '@/features/customization/application/services/CustomizationService';
import type { ProductCustomization } from '@/features/customization/domain/models/ProductCustomization';
import type { SizeOption } from '@/features/customization/utils/parseSizeList';
import {
  useCustomizationSheet,
  type CustomizationSheetContext,
} from '@/features/customization/presentation/hooks/useCustomizationSheet';
import { CustomizationDesktopOptions } from '@/features/customization/presentation/components/CustomizationDesktopOptions';

interface PdpDesktopCustomizationAccordionProps {
  service: CustomizationService;
  context: CustomizationSheetContext | null;
  selectedSize: SizeOption | null;
  onApplied: (result: {
    customization: ProductCustomization;
    selectedSize: SizeOption | null;
  }) => void;
  onCustomizeClick?: () => void;
  onCustomizationSelected?: (
    customizationType: string,
    appliedCustomization?: ProductCustomization,
  ) => void;
}

export function PdpDesktopCustomizationAccordion({
  service,
  context,
  selectedSize,
  onApplied,
  onCustomizeClick,
  onCustomizationSelected,
}: PdpDesktopCustomizationAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClose = useCallback(() => {
    setIsExpanded(false);
  }, []);

  const sheet = useCustomizationSheet({
    service,
    context,
    selectedSize,
    isOpen: isExpanded,
    onClose: handleClose,
  });

  const handleToggle = () => {
    setIsExpanded((prev) => {
      if (!prev) {
        onCustomizeClick?.();
      }
      return !prev;
    });
  };

  const handleSelect = async (key: string, value: string) => {
    const result = await sheet.handleSelectAndApply(key, value);
    if (result) {
      onApplied(result);
      onCustomizationSelected?.(key, result.customization);
    }
  };

  if (!context) return null;

  return (
    <div className="hidden border-y border-[#d9d9d9] md:block">
      <button
        type="button"
        onClick={handleToggle}
        aria-expanded={isExpanded}
        className="flex w-full items-center justify-between border-0 bg-[#f0f0f0] px-0 py-3 text-left text-sm font-semibold text-[#1a2b6d]"
      >
        <span>{isExpanded ? 'Hide Customization' : 'Customize this design'}</span>
        {isExpanded ? (
          <Minus className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
        ) : (
          <Plus className="h-4 w-4 shrink-0" strokeWidth={2} aria-hidden />
        )}
      </button>

      <div
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none',
          isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          <CustomizationDesktopOptions
            draft={sheet.draft}
            parsedOptions={sheet.parsedOptions}
            goldPurities={sheet.goldPurities}
            isApplying={sheet.isApplying || sheet.isPreviewLoading}
            onSelect={handleSelect}
          />
        </div>
      </div>
    </div>
  );
}
